import React, { useEffect, useState } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import BenefitsScreen from "src/screens/benefitsScreen";
import {
  Accordion,
  ActivateCreditCard,
  TaxRefund,
  TaxRefundIframe,
} from "src/components";
import UpdateContactDetails from "src/components/updateContactDetails";
import { ROUTES, URL_SOURCE_TYPE } from "src/utils/enums";
import { ChoosePolicy, Login, OTP } from "src/components/loginSteps";
import { useAppDispatch, useAppSelector } from "src/hooks/useRedux";
import {
  EligibilityRouter,
  LoginScreen,
  MustListScreen,
  PcPlusScreen,
  SummaryScreen,
  TestingScreen,
} from "src/screens";
import { images } from "src/assets/images/images";
import { setHostUrl } from "src/redux/slices/appSlice/AppSlice";
import Success from "src/components/iframe/success";
import Failure from "src/components/iframe/failure";
import { eligibilityRoutes, pcPlusRoutes } from "src/utils/consts";
import { sentPostMessage } from "src/utils/utils";

// Component to conditionally render TestingScreen only in dev or staging environments
const DevStagingRoute: React.FC = () => {
  const currentUrl = window.location.href.toLowerCase();
  const isDevOrStaging =
    currentUrl.includes("dev") ||
    currentUrl.includes("staging") ||
    currentUrl.includes("localhost");

  return isDevOrStaging ? <TestingScreen /> : <Navigate to="/" replace />;
};

const AppRoutes: React.FC = () => {
  const { strings, firstSectionValid, secondSectionValid, thirdSectionValid, locationRedirectEnabled } =
    useAppSelector((state) => state.app);
  const [refreshHeight, setRefreshHeight] = useState(false);
  const dispatch = useAppDispatch();
  const location = useLocation();

  useEffect(() => {
    const pathParts = location.pathname.split("/");
    const source = pathParts[1];
    dispatch(setHostUrl(source));
    console.log("locationRedirectEnabled", locationRedirectEnabled);
    // Temporary redirect logic for specific pc-pay entry points
    if (locationRedirectEnabled) {
      const normalizedPath = location.pathname.toLowerCase();
      // Strip potential trailing slash and query params for comparison
      const cleanPath = normalizedPath.split("?")[0].replace(/\/$/, "");
      const shouldRedirect = [
        "/sms/pc-pay",
        "/app/pc-pay",
        "/sms-share/pc-pay",
      ].some((allowedPath) => cleanPath === allowedPath);

      if (shouldRedirect) {
        window.location.replace(
          "https://www.passportcard.co.il/financial-services/p-soon/"
        );
        return; // prevent further execution if redirecting
      }
    }

    if (
      source === URL_SOURCE_TYPE.DIGITAL ||
      source === URL_SOURCE_TYPE.DIGITAL_AGENT
    ) {
      const data = {
        message: "ROUTE_CHANGED",
        pathname: window.location.pathname,
      } as const;
      sentPostMessage(data);
    }
  }, [location, dispatch, locationRedirectEnabled]);

  const onRefreshHeight = () => {
    setRefreshHeight(!refreshHeight);
  };

  return (
    <Routes>
      <Route path={ROUTES.TESTING_SCREEN} element={<DevStagingRoute />} />

      {/* Add this route at the top to handle the root path */}
      <Route
        path="/"
        element={<Navigate to={`/${URL_SOURCE_TYPE.DIGITAL}`} replace />}
      />

      <Route
        path={`/:source/${ROUTES.SUMMARY_SCREEN}`}
        element={<SummaryScreen />}
      />
      <Route
        path={`/:source/${ROUTES.MUST_LIST_SCREEN}`}
        element={<MustListScreen />}
      />

      <Route path="/:source" element={<BenefitsScreen />}>
        <Route
          path={ROUTES.UPDATE_CONTACT_DETAILS}
          element={
            <Accordion
              route={ROUTES.UPDATE_CONTACT_DETAILS}
              index={0}
              title={strings.t_benefits_update_contact_info}
              isDone={firstSectionValid}
              accordionIcon={images.left}
            >
              <UpdateContactDetails />
            </Accordion>
          }
        />
        <Route
          path={ROUTES.ACTIVATE_CREDIT_CARD}
          element={
            <Accordion
              route={ROUTES.ACTIVATE_CREDIT_CARD}
              refreshHeight
              index={1}
              title={strings.t_benefits_enable_card}
              isDone={secondSectionValid}
              accordionIcon={images.left}
            >
              <ActivateCreditCard onRefreshHeight={onRefreshHeight} />
            </Accordion>
          }
        />
        <Route
          path={ROUTES.TAX_REFAUND}
          element={
            <Accordion
              route={ROUTES.TAX_REFAUND}
              index={2}
              title={strings.t_benefits_accordion_title}
              isDone={thirdSectionValid}
              accordionIcon={images.left}
            >
              <TaxRefund />
            </Accordion>
          }
        />
      </Route>
      <Route
        path={`/${URL_SOURCE_TYPE.DIGITAL}/${ROUTES.TAX_REFAUND_IFRAME}`}
        element={<TaxRefundIframe />}
      />

      <Route path="/:source">
        <Route path={ROUTES.LOGIN} element={<LoginScreen />}>
          <Route
            index
            element={<Navigate to={ROUTES.LOGIN_DETAILS} replace />}
          />
          <Route path={ROUTES.LOGIN_DETAILS} element={<Login />} />
          <Route path={ROUTES.LOGIN_OTP} element={<OTP />} />
          <Route path={ROUTES.LOGIN_CHOOSE_POLICY} element={<ChoosePolicy />} />
        </Route>
      </Route>
      {/* <Route
        path={`${ROUTES.DIGITAL}/${ROUTES.TAX_REFAUND_IFRAME}`}
        element={<TaxRefundIframe />}
      /> */}
      <Route path="/:source" element={<EligibilityRouter />}>
        {eligibilityRoutes.map((route) => (
          <Route key={route} path={route} element={<EligibilityRouter />} />
        ))}

        <Route path={""} element={<EligibilityRouter />} />
      </Route>
      {/* Generic dynamic routing */}
      <Route
        path={`${URL_SOURCE_TYPE.ENSURE}/:subRoute`}
        element={<EligibilityRouter />}
      />
      <Route
        path={`${URL_SOURCE_TYPE.PFS_REP}/:subRoute`}
        element={<EligibilityRouter />}
      />
      <Route
        path={`/${URL_SOURCE_TYPE.DIGITAL}/${ROUTES.TAX_REFAUND_IFRAME}`}
        element={<EligibilityRouter />}
      />

      {[URL_SOURCE_TYPE.PFS_REP, URL_SOURCE_TYPE.REP_MANUAL].map((route) => (
        <Route
          key={route}
          path={`${route}/${ROUTES.MUST_LIST_SCREEN}`}
          element={<MustListScreen />}
        />
      ))}
      {pcPlusRoutes.map((route) => (
        <Route
          key={route}
          path={`${route}/${ROUTES.PC_PLUS_SCREEN}`}
          element={<PcPlusScreen />}
        />
      ))}

      {/* add route for success and failure */}
      <Route path="/:source/:section/success" element={<Success />} />

      <Route path="/:source/:section/failure" element={<Failure />} />

      {/* <Route path="*" element={<Navigate to={ROUTES.CAMPAIGN} replace />} /> */}
    </Routes>
  );
};

export default AppRoutes;
